<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import Modal from 'dashboard/components/Modal.vue';
import CannedResponseForm from './CannedResponseForm.vue';

const props = defineProps({
  id: { type: Number, default: null },
  edcontent: { type: String, default: '' },
  edshortCode: { type: String, default: '' },
  edcontentType: { type: String, default: 'markdown' },
  edcustomRoleIds: { type: Array, default: () => [] },
  edfiles: { type: Array, default: () => [] },
  onClose: { type: Function, default: () => {} },
});

const { t } = useI18n();
const store = useStore();

const show = ref(true);
const isSaving = ref(false);
const shortCode = ref(props.edshortCode);
const content = ref(props.edcontent);
const contentType = ref(props.edcontentType);
const selectedRoles = ref([]);
const attachmentIds = ref(props.edfiles.map(file => file.signed_id));

const customRoleOptions = useMapGetter('customRole/getCustomRoles');

// The record only carries role ids; the picker wants the full role objects,
// which may only arrive once the roles finish loading.
watch(
  customRoleOptions,
  roles => {
    selectedRoles.value = roles.filter(role =>
      props.edcustomRoleIds.includes(role.id)
    );
  },
  { immediate: true }
);

const v$ = useVuelidate(
  {
    shortCode: { required, minLength: minLength(2) },
    content: { required },
  },
  { shortCode, content }
);

const shortCodeError = computed(() => v$.value.shortCode.$invalid);
const contentError = computed(() => v$.value.content.$invalid);
const pageTitle = computed(
  () => `${t('CANNED_MGMT.EDIT.TITLE')} - ${props.edshortCode}`
);

onMounted(() => {
  if (!customRoleOptions.value.length)
    store.dispatch('customRole/getCustomRole');
});

const editCannedResponse = async () => {
  isSaving.value = true;
  try {
    await store.dispatch('updateCannedResponse', {
      id: props.id,
      short_code: shortCode.value,
      content: content.value,
      content_type: contentType.value,
      custom_role_ids: selectedRoles.value.map(role => role.id),
      blob_ids: attachmentIds.value,
    });
    useAlert(t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    props.onClose();
  } catch (error) {
    useAlert(error?.message || t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <CannedResponseForm
        v-model:short-code="shortCode"
        v-model:content="content"
        v-model:content-type="contentType"
        v-model:selected-roles="selectedRoles"
        v-model:attachment-ids="attachmentIds"
        :short-code-error="shortCodeError"
        :content-error="contentError"
        :is-loading="isSaving"
        :custom-role-options="customRoleOptions"
        :initial-files="edfiles"
        @short-code-blur="v$.shortCode.$touch"
        @content-blur="v$.content.$touch"
        @submit="editCannedResponse"
        @cancel="onClose"
      />
    </div>
  </Modal>
</template>
